import {CUSTOM_PLAN_CODE} from 'shells/billing/add-ons/helpers';
import type {
    Cycle,
    Plan,
    SelectedAddOn,
    Product,
    AddOns,
    BillingRoute,
    CachedPlan,
} from 'shells/billing/types';
import type {Breadcrumb} from 'shells/numbered-breadcrumbs/numbered-breadcrumbs';

import type {
    GetSessionUser_loggedInUser as User,
    GetInvoicePreview_invoicePreview as InvoicePreview,
    GetAccountBilling_accountBilling as AccountBilling,
} from 'nutshell-graphql-types';

import {
    ENGAGEMENT_PRO_MONTHLY,
    MARKETING_PRO_MONTHLY,
    // @ts-expect-error
} from '../../../../client_modules/shells/react/components/billing/consts';
import {appHistory} from '../../../../history';

import type {ManageBreadcrumb} from './manage-breadcrumbs';

export function getRouteFromPath(path: string): BillingRoute {
    switch (path) {
        case '/setup/billing/sales':
            return 'sales';
        case '/setup/billing/marketing':
            return 'marketing';
        case '/setup/billing/engagement':
            return 'engagement';
        case '/setup/billing/add-ons':
            return 'add-ons';
        case '/setup/billing/review':
            return 'review';
        case '/setup/billing/success':
            return 'success';
        case '/setup/billing/churn':
            return 'churn';
    }

    throw new Error(`Invalid billing path: ${path}`);
}

export function getPlanCodeForGivenCycle(planCode: string, cycle: Cycle) {
    const planCodeComponents = planCode.split('-');

    // We expect the plan code to have at least 2 hyphens (ex: ns-uub-a)
    // So we don't crash the page, we should return the original plan code
    if (planCodeComponents.length < 3) {
        return planCode;
    }

    if (cycle === 'MONTHLY') {
        planCodeComponents[2] = 'm';
    } else {
        planCodeComponents[2] = 'a';
    }

    return planCodeComponents.join('-');
}

export function getCrmPlanDetails(
    selectedPlanCode: string | null | undefined,
    plans: Plan[],
    numUsers?: string | null
): string | null | undefined {
    if (!selectedPlanCode || !numUsers) {
        return null;
    }

    const selectedPlan = plans.find((plan) => plan.code === selectedPlanCode);
    const numUsersDetails = `${numUsers} ${numUsers === '1' ? 'user' : 'users'}`;

    return selectedPlan && selectedPlan.shortenedName
        ? `${selectedPlan.shortenedName} - ${numUsersDetails} `
        : null;
}

export function getEngagementPlanDetails(
    selectedPlanCode: string | null | undefined,
    plans: Plan[],
    numUsers?: string | null
): string | null | undefined {
    if (!selectedPlanCode || !numUsers) {
        return null;
    }

    const numUsersDetails = `${numUsers} ${numUsers === '1' ? 'user' : 'users'}`;

    const selectedPlan = plans.find((plan) => plan.code === selectedPlanCode);

    return selectedPlan && selectedPlan.shortenedName
        ? `${selectedPlan.shortenedName} - ${numUsersDetails} `
        : null;
}

export function getMarketingPlanDetails(
    selectedPlanCode: string | null | undefined,
    plans: Plan[]
): string | null | undefined {
    if (!selectedPlanCode) {
        return null;
    }

    const selectedPlan = plans.find((plan) => plan.code === selectedPlanCode);

    return selectedPlan && selectedPlan.shortenedName;
}

/**
 * Used to clean up selected add-ons prior to sending them to the server.
 * Backend is not aware of custom plans because these are defined with the
 * support team. While the user waits for the plan to be defined, we need to
 * maintain the active plan (if one exists).
 */
export function replaceCustomPlansWithActivePlans(
    selectedAddOns?: SelectedAddOn[] | null,
    addOns?: AddOns | null
): SelectedAddOn[] {
    if (!selectedAddOns || !addOns) {
        return [];
    }

    const selectedAddOnsWithoutCustomPlans = [];
    for (const selectedAddOn of selectedAddOns) {
        if (selectedAddOn.planCode === CUSTOM_PLAN_CODE) {
            const activePlanCode = getActivePlanCode(selectedAddOn.product, addOns);
            if (activePlanCode) {
                const updatedSelectedAddOn = {...selectedAddOn, planCode: activePlanCode} as const;
                selectedAddOnsWithoutCustomPlans.push(updatedSelectedAddOn);
            }
        } else {
            selectedAddOnsWithoutCustomPlans.push(selectedAddOn);
        }
    }

    return selectedAddOnsWithoutCustomPlans;
}

function getActivePlanCode(product: Product, addOns: AddOns): string | null | undefined {
    switch (product) {
        case 'MARKETING':
            return addOns.marketing.activePlanCode;
        case 'NUTSHELL_IQ':
            return addOns.nutshellIq.activePlanCode;
        case 'REVENUE_BOOSTER':
            return addOns.revenueBooster.activePlanCode;
        default:
            return null;
    }
}

export function isFakePlan(sku: string | null | undefined, plans: Plan[]): boolean {
    if (!sku) {
        return false;
    }

    const plan = plans.find((p) => p.code === sku);

    return Boolean(plan && plan.isFake);
}

/**
 * Filters out add-ons that are included for free based on the invoice preview.
 * Currently only Nutshell Advisor can be included for free (based on MRR
 * threshold and sales criteria).
 */
export function filterIncludedAddOns(
    selectedAddOns: SelectedAddOn[],
    invoicePreview?: InvoicePreview | null
): SelectedAddOn[] {
    const isNutshellAdvisorIncluded =
        invoicePreview &&
        invoicePreview.plan &&
        invoicePreview.plan.nutshellAdvisor &&
        invoicePreview.plan.nutshellAdvisor.isFake;

    if (!isNutshellAdvisorIncluded) {
        return selectedAddOns;
    }

    return selectedAddOns.filter((addOn) => addOn.product !== 'NUTSHELL_ADVISOR');
}

/**
 * There are certain add-ons that are already included for free with other plans,
 * so it's best that we don't show them as options to the user.
 */
export function removeRedundantAddOns(
    addOns?: AddOns | null,
    selectedMarketingSuitePlanCode?: string | null,
    selectedEngagementPlanCode?: string | null
): AddOns | null | undefined {
    let newAddOns = addOns;

    if (!newAddOns) {
        return null;
    }

    // We don't want to allow purchasing additional SMS credits if they have no
    // way to spend them
    if (
        selectedEngagementPlanCode !== ENGAGEMENT_PRO_MONTHLY &&
        selectedMarketingSuitePlanCode !== MARKETING_PRO_MONTHLY
    ) {
        newAddOns = {
            ...newAddOns,
            smsCampaigns: {
                ...newAddOns.smsCampaigns,
                plans: [],
            },
        };
    }

    return newAddOns;
}

export function handlePlanIsEnabledChange(
    product: Product,
    planCode: string,
    setSelectedAddOns: (arg1: (prevSelectedAddOns: SelectedAddOn[]) => SelectedAddOn[]) => void,
    quantity?: number | null,
    keepEnabled?: boolean
) {
    setSelectedAddOns((prevSelectedAddOns: SelectedAddOn[]) => {
        const isEnabled = prevSelectedAddOns.find(
            (selectedAddOn) => selectedAddOn.product === product
        );

        if (isEnabled && keepEnabled) {
            return prevSelectedAddOns;
        } else if (isEnabled && !keepEnabled) {
            return prevSelectedAddOns.filter((selectedAddOn) => selectedAddOn.product !== product);
        } else {
            return [...prevSelectedAddOns, {product, planCode, quantity}];
        }
    });
}

export function handleSelectedPlanChange(
    product: Product,
    planCode: string,
    setSelectedAddOns: (arg1: (prevSelectedAddOns: SelectedAddOn[]) => SelectedAddOn[]) => void,
    quantity?: number
) {
    setSelectedAddOns((prevSelectedAddOns: SelectedAddOn[]) =>
        prevSelectedAddOns.map((prevSelectedAddOn) => {
            if (prevSelectedAddOn.product === product) {
                return {
                    ...prevSelectedAddOn,
                    planCode,
                    quantity: quantity || null,
                };
            }

            return prevSelectedAddOn;
        })
    );
}

export const MANAGE_SCROLL_CONTAINER_ID = 'manage-scroll-container';
export function getScrollContainerId(route: BillingRoute) {
    return `${MANAGE_SCROLL_CONTAINER_ID}-${route}`;
}

export function navigateToRoute(
    route?: BillingRoute,
    queryParams?: {
        [key: string]: string;
    }
) {
    const hash = route === 'success' ? '' : window.location.hash;
    if (!route) {
        appHistory.push('/setup/billing');
    } else {
        appHistory.push({
            pathname: `/setup/billing/${route}`,
            hash,
            search: queryParams ? `?${new URLSearchParams(queryParams).toString()}` : '',
        });
    }
}

/**
 * Maps the manage breadcrumb type to the breadcrumb type by generalizing the
 * type of the id field from BillingRoute to string.
 */
export function buildBreadcrumbs(manageBreadcrumbs: ManageBreadcrumb[]): Breadcrumb[] {
    return manageBreadcrumbs.map((bc) => ({...bc, id: bc.id as string}));
}

const ADD_ON_CACHE_INPUT_MAP: Partial<Record<Product, string>> = {
    MARKETING: 'marketing',
    QUOTES: 'quotes',
    NUTSHELL_IQ: 'nutshellIq',
    SMS_CAMPAIGNS: 'smsCampaigns',
    NUTSHELL_ADVISOR: 'nutshellAdvisor',
};
export function buildSelectedAddOnsCacheInput(selectedAddOns: SelectedAddOn[]): {
    marketing: CachedPlan | null | undefined;
    quotes: CachedPlan | null | undefined;
    nutshellIq: CachedPlan | null | undefined;
    smsCampaigns: CachedPlan | null | undefined;
    nutshellAdvisor: CachedPlan | null | undefined;
} {
    const cacheInput = {
        marketing: null,
        quotes: null,
        nutshellIq: null,
        smsCampaigns: null,
        nutshellAdvisor: null,
    } as const;

    for (const selectedAddOn of selectedAddOns) {
        // @ts-expect-error
        cacheInput[ADD_ON_CACHE_INPUT_MAP[selectedAddOn.product]] = {
            planCode: selectedAddOn.planCode,
        };
    }

    return cacheInput;
}

export function shouldShowShareButton(user?: User | null) {
    return user && user.isSupport;
}

export function getCrmPlanPriceDifference(
    accountBilling?: AccountBilling | null,
    invoicePreview?: InvoicePreview | null
): number {
    if (!accountBilling || !invoicePreview) {
        return 0;
    }

    const currentMonthlyTotal =
        accountBilling && accountBilling.currentPlan ? accountBilling.currentPlan.totalMonth : 0;

    const newMonthlyTotal =
        invoicePreview && invoicePreview.plan ? invoicePreview.plan.totalMonth : 0;

    return newMonthlyTotal - currentMonthlyTotal;
}
